import type { Mood } from 'shared';

/**
 * グローバル音エンジン（R2 感覚層・2026-06-21）
 *
 * 後続の全音響実装（StartScreen 統合・ミニゲーム19本・テンプレ配線）が依存する基盤。
 * フレームワーク非依存のシングルトン。React も DOM も前提にしない。
 *
 * 設計の核:
 *  - AudioContext は **1 個だけ**。全 SFX/heartbeat/ambient は master GainNode を経由する
 *    （mute / volume を 1 箇所で統括）。Template15 の「鳴らす度に new AudioContext」アンチパターンの是正版。
 *  - SFX は WebAudio 合成（音源ファイル不要）。BGM/アンビは別系統:
 *      BGM = HTMLAudio（アセット URL・ファイルは任意）/ ambient = 合成ドローン（ファイル不要）。
 *  - autoplay policy 対策: unlock() を初回ユーザージェスチャで呼ぶまで音は出せない。
 *    unlock 前・mute 中の playSfx/heartbeat/ambient は **安全に無視**（throw しない）。
 *  - SSR / window 不在（テスト）でも import 時・呼び出し時に throw しない。すべて遅延生成 + typeof window ガード。
 *
 * 公開 API は本ファイル末尾の `audioEngine` シングルトンを参照。
 */

export type SfxName =
  | 'success'
  | 'fail'
  | 'tap'
  | 'hint'
  | 'reveal'
  | 'select'
  | 'warning'
  | 'passage'
  | 'settle'
  | 'inscribe';

export interface HeartbeatHandle {
  /** 鼓動の速さ（beats per minute）を動的に変える。ミス増加で加速する等 */
  setBpm(bpm: number): void;
  /** 鼓動ループを止める。複数回呼んでも安全（冪等） */
  stop(): void;
}

export interface PlaySfxOptions {
  /** 場面の mood。音色（周波数/波形/減衰）が変わる。未指定は中庸トーン */
  mood?: Mood;
  /** この 1 発だけの相対音量（0..1）。master volume にさらに乗算される */
  volume?: number;
}

export interface PlaySfxAssetOptions {
  /** この 1 発だけの相対音量（0..1・既定 1）。master volume に乗算される */
  volume?: number;
}

export interface StartHeartbeatOptions {
  /** 初期 BPM（既定 60） */
  bpm?: number;
  /** mood。鼓動の基準周波数が変わる（dread はより低く重い） */
  mood?: Mood;
}

export interface PlayBgmOptions {
  /** 相対音量（0..1・既定 1）。master volume に乗算される */
  volume?: number;
  /** ループ再生するか（既定 true） */
  loop?: boolean;
  /** フェードイン時間 [ms]（既定 600） */
  fadeMs?: number;
}

export interface StopBgmOptions {
  /** フェードアウト時間 [ms]（既定 600） */
  fadeMs?: number;
}

/** volume/mute 変化を購読する UI 向けコールバック */
export type AudioChangeListener = (state: { volume: number; muted: boolean }) => void;

// ---- 永続化キー（仕様で固定） -------------------------------------------------
const LS_VOLUME_KEY = 'mg-audio-volume';
const LS_MUTED_KEY = 'mg-audio-muted';

// ---- 既定値 ------------------------------------------------------------------
const DEFAULT_VOLUME = 0.6;
const DEFAULT_MUTED = false;
const DEFAULT_BGM_FADE_MS = 600;
const DEFAULT_HEARTBEAT_BPM = 60;

/**
 * mood → SFX 音色テーブル。
 * 各 mood に「基準ピッチ係数 / 主たる波形 / 減衰の長さ係数」を持たせ、SfxName ごとの
 * ベース定義（下の SFX_BASE）に乗じて「それらしい」音を作る。過度に作り込まない。
 */
interface MoodTimbre {
  /** 周波数の倍率（1=中庸・<1 で低く重く・>1 で高く明るく） */
  pitch: number;
  /** その mood の主たる波形 */
  wave: OscillatorType;
  /** 減衰時間の倍率（>1 で長く残る・<1 で歯切れよく） */
  decay: number;
  /** 倍音(2倍)を薄く重ねる量（0..1）。mystic は高め */
  overtone: number;
}

const MOOD_TIMBRE: Record<Mood, MoodTimbre> = {
  // 静謐: 柔らかい正弦・小さめ・やや長い余韻
  stillness: { pitch: 0.92, wave: 'sine', decay: 1.15, overtone: 0.0 },
  // 緊迫: 中高域・歯切れよく短い立ち上がり
  tension: { pitch: 1.12, wave: 'sawtooth', decay: 0.7, overtone: 0.15 },
  // 神秘: やや高い倍音を含む
  mystic: { pitch: 1.05, wave: 'triangle', decay: 1.1, overtone: 0.5 },
  // 哀愁: 低めの正弦で長い余韻
  melancholy: { pitch: 0.85, wave: 'sine', decay: 1.3, overtone: 0.1 },
  // 恐怖: 低周波・不協・長い減衰
  dread: { pitch: 0.7, wave: 'sawtooth', decay: 1.5, overtone: 0.35 },
  // 軽妙: 明るい三角波
  playful: { pitch: 1.18, wave: 'triangle', decay: 0.8, overtone: 0.2 },
  // コミカル: より明るい三角波
  comedic: { pitch: 1.25, wave: 'triangle', decay: 0.75, overtone: 0.25 },
};

/** mood 未指定時に使う中庸トーン */
const NEUTRAL_TIMBRE: MoodTimbre = { pitch: 1, wave: 'sine', decay: 1, overtone: 0.1 };

/**
 * SfxName ごとのベース定義。
 * freq=基準周波数[Hz] / dur=基準長さ[s] / gain=基準ピーク音量(0..1) /
 * slideTo=終端周波数(null で固定・指定で freq→slideTo へ glide) / forceWave=波形固定（mood を無視したい時）
 */
interface SfxBase {
  freq: number;
  dur: number;
  gain: number;
  slideTo?: number | null;
  forceWave?: OscillatorType;
}

const SFX_BASE: Record<SfxName, SfxBase> = {
  // 成功: 上昇する明るい音
  success: { freq: 523.25, dur: 0.32, gain: 0.5, slideTo: 783.99 },
  // 失敗: 下降する沈む音
  fail: { freq: 311.13, dur: 0.42, gain: 0.5, slideTo: 155.56 },
  // タップ: 短いクリック
  tap: { freq: 440, dur: 0.06, gain: 0.32, forceWave: 'square' },
  // ヒント: 柔らかい上がり調子の知らせ
  hint: { freq: 587.33, dur: 0.22, gain: 0.34, slideTo: 698.46 },
  // 開示/発見: 余韻を持って広がる音
  reveal: { freq: 659.25, dur: 0.5, gain: 0.42, slideTo: 880 },
  // 選択: 軽い確定音
  select: { freq: 493.88, dur: 0.1, gain: 0.3 },
  // 警告: 低く詰まった注意音
  warning: { freq: 196, dur: 0.3, gain: 0.45, forceWave: 'sawtooth' },
  // P8-1 継ぎ目の儀式（シェル用・控えめ）:
  // 突入スティング: 低域からゆっくり持ち上がる（ミニゲームへ「潜る」含意）
  passage: { freq: 196, dur: 0.5, gain: 0.32, slideTo: 392 },
  // 復帰リリース: なだらかに降りて着地する（シェルへ「戻る」含意）
  settle: { freq: 523.25, dur: 0.55, gain: 0.28, slideTo: 329.63 },
  // 記帳: 台帳へ書き留める小さな一筆（図鑑記録の瞬間・ごく短い）
  inscribe: { freq: 1046.5, dur: 0.16, gain: 0.22, slideTo: 783.99, forceWave: 'triangle' },
};

/** mood → ambient ドローンの定義。null は「無音でよい」mood */
interface AmbientDrone {
  /** 基音 [Hz] */
  freq: number;
  /** 不協のために重ねる第2音 [Hz]（null で単音） */
  beatFreq: number | null;
  /** ドローンの音量（master 経由・極薄） */
  gain: number;
  wave: OscillatorType;
}

const MOOD_AMBIENT: Record<Mood, AmbientDrone | null> = {
  // 極薄の低音
  stillness: { freq: 110, beatFreq: null, gain: 0.025, wave: 'sine' },
  // 不穏: 中低域に微妙なうなり
  tension: { freq: 98, beatFreq: 101.5, gain: 0.05, wave: 'sawtooth' },
  // 神秘: 高めの倍音ドローン
  mystic: { freq: 146.83, beatFreq: 220, gain: 0.04, wave: 'triangle' },
  // 哀愁: 低い余韻
  melancholy: { freq: 87.31, beatFreq: null, gain: 0.035, wave: 'sine' },
  // 恐怖: 最も低く不協なドローン
  dread: { freq: 55, beatFreq: 58.27, gain: 0.06, wave: 'sawtooth' },
  // 軽妙/コミカルはアンビ無音
  playful: null,
  comedic: null,
};

const AMBIENT_FADE_MS = 800;

/** localStorage を安全に読む（SSR / 無効環境で throw しない） */
function safeReadStorage(key: string): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** localStorage を安全に書く（SSR / 無効環境で throw しない） */
function safeWriteStorage(key: string, value: string): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(key, value);
  } catch {
    /* quota 超過・プライベートモード等は黙って無視 */
  }
}

/** window の AudioContext コンストラクタを取得（webkit prefix も考慮）。不在なら null */
function getAudioContextCtor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

/**
 * 相対 URL を絶対 URL 化する（HTMLAudioElement.src との比較用）。
 * DOM 不在・不正 URL では入力をそのまま返す（比較が false になるだけで安全）。
 */
function toAbsoluteUrl(url: string): string {
  try {
    const base = typeof document !== 'undefined' ? document.baseURI : undefined;
    return new URL(url, base).href;
  } catch {
    return url;
  }
}

/**
 * グローバル音エンジン本体。
 *
 * すべて遅延初期化。new しても（import 時に）AudioContext は作らない。
 * unlock() が呼ばれて初めて AudioContext を生成 or resume する。
 */
class AudioEngine {
  // ---- 状態（永続化値は遅延ロード） ----
  private volume = DEFAULT_VOLUME;
  private muted = DEFAULT_MUTED;
  private stateLoaded = false;

  // ---- WebAudio コア（unlock まで null） ----
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private unlocked = false;

  // ---- ambient（合成ドローン） ----
  private ambientMood: Mood | null = null;
  private ambientNodes: { oscs: OscillatorNode[]; gain: GainNode } | null = null;
  // P8-1: ambient の一時減衰係数（0..1）。ending 到達などで「一段静める」ために使う。
  // mood 既定 gain に乗算されるだけで、mood/ドローン構成そのものは変えない。
  private ambientDuck = 1;

  // ---- BGM（HTMLAudio・master とは別系統） ----
  private bgmEl: HTMLAudioElement | null = null;
  private bgmBaseVolume = 1;
  private bgmFadeRaf: number | null = null;
  // P8-1: unlock 前（autoplay policy）に play() が reject された BGM を覚えておき、
  // unlock() 時に再試行するフラグ。従来は reject を握りつぶすだけで、以後 URL が
  // 変わるまで BGM が永久に鳴らない死路だった（unlock は ambient しか再点火しない）。
  private bgmRetryPending = false;

  // ---- heartbeat ハンドル群（master mute 連動のため保持） ----
  private heartbeats = new Set<InternalHeartbeat>();

  // ---- 変更購読 ----
  private listeners = new Set<AudioChangeListener>();

  /** 永続化された volume/muted を 1 回だけ遅延ロードする */
  private ensureStateLoaded(): void {
    if (this.stateLoaded) return;
    this.stateLoaded = true;
    const rawVol = safeReadStorage(LS_VOLUME_KEY);
    if (rawVol != null) {
      const parsed = Number.parseFloat(rawVol);
      if (Number.isFinite(parsed)) this.volume = clamp01(parsed);
    }
    const rawMuted = safeReadStorage(LS_MUTED_KEY);
    if (rawMuted != null) this.muted = rawMuted === 'true';
  }

  /** master gain に「実効音量（mute 込み）」を反映する */
  private applyMasterGain(): void {
    if (!this.masterGain || !this.ctx) return;
    const effective = this.muted ? 0 : this.volume;
    // クリックノイズ回避のため短いランプで設定
    const now = this.ctx.currentTime;
    const g = this.masterGain.gain;
    try {
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(effective, now + 0.02);
    } catch {
      g.value = effective;
    }
  }

  /** listeners へ現在の状態を通知 */
  private emitChange(): void {
    const snapshot = { volume: this.volume, muted: this.muted };
    for (const cb of this.listeners) {
      try {
        cb(snapshot);
      } catch {
        /* リスナーの例外は他へ波及させない */
      }
    }
  }

  // ========================================================================
  // unlock / 状態
  // ========================================================================

  /**
   * 初回ユーザージェスチャ（pointerdown/keydown/touchstart）で呼ぶ。
   * AudioContext を生成（or suspended なら resume）し、master GainNode を張る。
   * 2 回目以降は resume を試みるだけ（冪等）。
   */
  unlock(): void {
    this.ensureStateLoaded();
    const Ctor = getAudioContextCtor();
    if (!Ctor) return; // window 不在・WebAudio 非対応は黙って無視

    if (!this.ctx) {
      try {
        this.ctx = new Ctor();
        this.masterGain = this.ctx.createGain();
        this.masterGain.gain.value = this.muted ? 0 : this.volume;
        this.masterGain.connect(this.ctx.destination);
      } catch {
        this.ctx = null;
        this.masterGain = null;
        return;
      }
    }

    // suspended（autoplay policy）なら resume を試みる
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume().catch(() => {
        /* resume 失敗は次のジェスチャで再試行される */
      });
    }
    this.unlocked = true;
    this.applyMasterGain();

    // unlock 前に setAmbient されていた mood があれば、ここで発音を起こす
    if (this.ambientMood && !this.ambientNodes) {
      this.startAmbientNodes(this.ambientMood);
    }

    // P8-1: unlock 前に play() が reject された BGM をここで再試行する（ambient と対称）。
    this.retryPendingBgm();
  }

  isUnlocked(): boolean {
    return this.unlocked && this.ctx != null && this.ctx.state === 'running';
  }

  // ========================================================================
  // master volume / mute
  // ========================================================================

  setMasterVolume(v: number): void {
    this.ensureStateLoaded();
    const next = clamp01(v);
    this.volume = next;
    safeWriteStorage(LS_VOLUME_KEY, String(next));
    this.applyMasterGain();
    this.applyBgmVolume();
    this.emitChange();
  }

  getMasterVolume(): number {
    this.ensureStateLoaded();
    return this.volume;
  }

  setMuted(m: boolean): void {
    this.ensureStateLoaded();
    this.muted = !!m;
    safeWriteStorage(LS_MUTED_KEY, String(this.muted));
    this.applyMasterGain();
    this.applyBgmMuteState();
    this.emitChange();
  }

  isMuted(): boolean {
    this.ensureStateLoaded();
    return this.muted;
  }

  /** volume/mute 変化を購読する。返り値の関数を呼ぶと購読解除 */
  onChange(cb: AudioChangeListener): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  /** いま発音してよいか（unlock 済み・非 mute・ctx 稼働） */
  private canSound(): boolean {
    return this.isUnlocked() && !this.muted && this.masterGain != null && this.ctx != null;
  }

  // ========================================================================
  // SFX（WebAudio 合成）
  // ========================================================================

  /**
   * 合成 SFX を鳴らす。mood で音色（周波数/波形/減衰）が変わる。
   * unlock 前・mute 中は安全に無視（throw しない）。
   */
  playSfx(name: SfxName, opts?: PlaySfxOptions): void {
    if (!this.canSound()) return;
    const ctx = this.ctx;
    const master = this.masterGain;
    if (!ctx || !master) return;

    const base = SFX_BASE[name];
    if (!base) return;
    const timbre = opts?.mood ? MOOD_TIMBRE[opts.mood] : NEUTRAL_TIMBRE;
    const relVol = clamp01(opts?.volume ?? 1);

    try {
      const now = ctx.currentTime;
      const dur = base.dur * timbre.decay;
      const startFreq = base.freq * timbre.pitch;
      const endFreq = base.slideTo != null ? base.slideTo * timbre.pitch : null;
      const wave = base.forceWave ?? timbre.wave;
      const peak = base.gain * relVol;

      // ノート用の局所 gain（master の下にぶら下げる）
      const noteGain = ctx.createGain();
      noteGain.connect(master);
      noteGain.gain.setValueAtTime(0.0001, now);
      noteGain.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), now + 0.008);
      noteGain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

      // 主オシレータ
      const osc = ctx.createOscillator();
      osc.type = wave;
      osc.frequency.setValueAtTime(startFreq, now);
      if (endFreq != null) {
        osc.frequency.exponentialRampToValueAtTime(Math.max(endFreq, 1), now + dur);
      }
      osc.connect(noteGain);
      osc.start(now);
      osc.stop(now + dur + 0.02);

      // 倍音（mood により薄く重ねる）
      let overtoneOsc: OscillatorNode | null = null;
      if (timbre.overtone > 0) {
        overtoneOsc = ctx.createOscillator();
        overtoneOsc.type = 'sine';
        overtoneOsc.frequency.setValueAtTime(startFreq * 2, now);
        if (endFreq != null) {
          overtoneOsc.frequency.exponentialRampToValueAtTime(Math.max(endFreq * 2, 1), now + dur);
        }
        const ovGain = ctx.createGain();
        ovGain.gain.value = timbre.overtone;
        overtoneOsc.connect(ovGain);
        ovGain.connect(noteGain);
        overtoneOsc.start(now);
        overtoneOsc.stop(now + dur + 0.02);
      }

      // 後始末（GC 任せでも良いが明示 disconnect）
      osc.onended = () => {
        try {
          osc.disconnect();
          overtoneOsc?.disconnect();
          noteGain.disconnect();
        } catch {
          /* 既に切れていれば無視 */
        }
      };
    } catch {
      /* 合成失敗は無視（音が出ないだけ） */
    }
  }

  /**
   * アセット音源（URL）を 1 発だけ鳴らす（合成 SFX ではなくファイル）。
   * R2 Wave2C で `config.audio.successSE/failSE/tapSE` を「指定があればアセット音・無ければ合成音」に
   * できるようにするための土台。**現状はテンプレへ広く配線していない**（呼び出し口を用意するだけ）。
   *
   * 設計（合成 SFX の playSfx と同じ契約）:
   *  - master mute/volume に連動する。mute 中は鳴らさない（再生開始しない）。
   *    音量は master volume × 相対音量。BGM とは別の使い捨て HTMLAudioElement を都度作って投げ捨てる。
   *  - 空 URL / 再生失敗 / Audio 非対応は **安全に無視**（throw しない）。
   *  - unlock は不要（HTMLAudio は WebAudio の AudioContext を介さない）。ただし autoplay 制限下では
   *    ユーザージェスチャ前の play() が reject されうる → その場合も握りつぶす。
   */
  playSfxAsset(url: string, opts?: PlaySfxAssetOptions): void {
    this.ensureStateLoaded();
    const trimmed = (url ?? '').trim();
    if (!trimmed) return;
    // master mute 中は鳴らさない（合成 SFX の canSound 相当の入口ガード）。
    if (this.muted) return;
    if (typeof Audio === 'undefined') return;

    const relVol = clamp01(opts?.volume ?? 1);
    try {
      const el = new Audio();
      el.src = trimmed;
      el.loop = false;
      el.volume = clamp01(this.volume * relVol);
      // 終了・失敗で参照を手放す（GC 任せだが src も外して確実に解放）。
      const release = (): void => {
        try {
          el.src = '';
        } catch {
          /* 無視 */
        }
      };
      el.onended = release;
      el.onerror = release;
      void el.play().catch(() => {
        /* autoplay 制限・URL 不正は無視 */
        release();
      });
    } catch {
      /* Audio 生成失敗は無視（音が出ないだけ） */
    }
  }

  /**
   * P8-1: 起動「……接続」演出の音（世界観 §7.4: 台詞ではなく“現象”の音）。
   * 低いドローンがゆっくり立ち上がり、終盤にか細い応答が一瞬だけ載って収束する。
   * durationMs は SupernaturalProvider.playConnectionSequence の実 duration を渡し、
   * 視覚の収束と同じ長さで消えるようにする。unlock 前・mute 中は安全に無視。
   * 全ノードは master gain 経由（ミュート/音量の一元支配を崩さない）。
   */
  playConnectionCue(durationMs: number): void {
    // 呼び出し元（handleStartGame）は unlock() 直後＝resume が完了していない可能性があるため、
    // canSound()（state==='running' 必須）ではなく unlocked+非mute のみで判定する。
    // suspended のままスケジュールしても、resume 完了時点から予定通り再生される。
    this.ensureStateLoaded();
    if (!this.unlocked || this.muted) return;
    const ctx = this.ctx;
    const master = this.masterGain;
    if (!ctx || !master) return;
    try {
      const now = ctx.currentTime;
      const dur = Math.max(0.3, Math.min(4, durationMs / 1000));

      // 1) 低いドローンの立ち上がり（49Hz → 82Hz・控えめなピーク 0.09）
      const droneGain = ctx.createGain();
      droneGain.connect(master);
      droneGain.gain.setValueAtTime(0.0001, now);
      droneGain.gain.linearRampToValueAtTime(0.09, now + dur * 0.55);
      droneGain.gain.linearRampToValueAtTime(0.0001, now + dur);
      const drone = ctx.createOscillator();
      drone.type = 'sine';
      drone.frequency.setValueAtTime(49, now);
      drone.frequency.exponentialRampToValueAtTime(82, now + dur);
      drone.connect(droneGain);
      drone.start(now);
      drone.stop(now + dur + 0.05);
      drone.onended = () => {
        try {
          drone.disconnect();
          droneGain.disconnect();
        } catch {
          /* 無視 */
        }
      };

      // 2) か細い応答（終盤 72% 地点に一瞬だけ・ピーク 0.05）
      const resAt = now + dur * 0.72;
      const resGain = ctx.createGain();
      resGain.connect(master);
      resGain.gain.setValueAtTime(0.0001, resAt);
      resGain.gain.linearRampToValueAtTime(0.05, resAt + 0.03);
      resGain.gain.exponentialRampToValueAtTime(0.0001, resAt + 0.3);
      const res = ctx.createOscillator();
      res.type = 'triangle';
      res.frequency.setValueAtTime(587.33, resAt);
      res.connect(resGain);
      res.start(resAt);
      res.stop(resAt + 0.35);
      res.onended = () => {
        try {
          res.disconnect();
          resGain.disconnect();
        } catch {
          /* 無視 */
        }
      };
    } catch {
      /* 合成失敗は無視（音が出ないだけ） */
    }
  }

  // ========================================================================
  // heartbeat（共有 ctx で鼓動ループ）
  // ========================================================================

  /**
   * 鼓動ループを開始しハンドルを返す。Template15 の 60/50Hz 二拍を一般化したもの。
   * setInterval は **1 本**。鳴らす度に短命 osc を作って stop する（共有 ctx 上で）。
   * unlock 前・mute 中でも handle は返る（鳴らないだけ）。後で unlock すれば鳴り始める。
   */
  startHeartbeat(opts?: StartHeartbeatOptions): HeartbeatHandle {
    const hb = new InternalHeartbeat(this, opts?.bpm ?? DEFAULT_HEARTBEAT_BPM, opts?.mood);
    this.heartbeats.add(hb);
    hb.start();
    return hb;
  }

  /** heartbeat 内部から「いま 1 拍鳴らす」ために呼ぶ。canSound でなければ何もしない */
  beatPulse(baseFreq: number): void {
    if (!this.canSound()) return;
    const ctx = this.ctx;
    const master = this.masterGain;
    if (!ctx || !master) return;
    try {
      const now = ctx.currentTime;
      // 1 拍 = 二連音（lub-dub）。Template15 の 60Hz→50Hz を mood 基準に一般化
      this.thump(ctx, master, baseFreq, now, 0.3, 0.15);
      this.thump(ctx, master, baseFreq * 0.83, now + 0.1, 0.2, 0.1);
    } catch {
      /* 無視 */
    }
  }

  /** 1 つの低音ドスンを生成する内部ヘルパ */
  private thump(
    ctx: AudioContext,
    master: GainNode,
    freq: number,
    at: number,
    peak: number,
    dur: number,
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(peak, at);
    gain.gain.exponentialRampToValueAtTime(0.01, at + dur);
    osc.connect(gain);
    gain.connect(master);
    osc.start(at);
    osc.stop(at + dur);
    osc.onended = () => {
      try {
        osc.disconnect();
        gain.disconnect();
      } catch {
        /* 無視 */
      }
    };
  }

  /** heartbeat が stop した時に集合から外す（内部用） */
  forgetHeartbeat(hb: InternalHeartbeat): void {
    this.heartbeats.delete(hb);
  }

  // ========================================================================
  // ambient（合成ドローン・mood 連動）
  // ========================================================================

  /**
   * mood 連動アンビエント（合成ドローン）。アセット不要。
   * 同じ mood を再指定したら何もしない。別 mood なら差し替え。null で停止。
   * unlock 前に呼ばれても mood を覚えておき、unlock 時に発音を起こす。
   */
  setAmbient(mood: Mood | null): void {
    if (mood === this.ambientMood) return;
    this.ambientMood = mood;

    // 既存ドローンをフェードアウト停止
    this.stopAmbientNodes();

    if (mood == null) return;
    // unlock 前なら mood を覚えただけ（unlock 時に startAmbientNodes される）
    if (this.canSound()) {
      this.startAmbientNodes(mood);
    }
  }

  /** 実際にドローンの osc 群を起こす（内部用） */
  private startAmbientNodes(mood: Mood): void {
    const drone = MOOD_AMBIENT[mood];
    if (!drone) return; // playful/comedic は無音
    if (!this.canSound()) return;
    const ctx = this.ctx;
    const master = this.masterGain;
    if (!ctx || !master) return;
    try {
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.linearRampToValueAtTime(
        Math.max(drone.gain * this.ambientDuck, 0.0001),
        now + AMBIENT_FADE_MS / 1000,
      );
      gain.connect(master);

      const oscs: OscillatorNode[] = [];
      const o1 = ctx.createOscillator();
      o1.type = drone.wave;
      o1.frequency.value = drone.freq;
      o1.connect(gain);
      o1.start(now);
      oscs.push(o1);

      if (drone.beatFreq != null) {
        const o2 = ctx.createOscillator();
        o2.type = drone.wave;
        o2.frequency.value = drone.beatFreq;
        o2.connect(gain);
        o2.start(now);
        oscs.push(o2);
      }

      this.ambientNodes = { oscs, gain };
    } catch {
      this.ambientNodes = null;
    }
  }

  /**
   * P8-1: ambient を一時的に「一段静める」（ending 到達の余韻など）。
   * scale は mood 既定 gain への乗算係数（1=通常・0.4=静める・0=実質無音）。
   * 鳴っていない時に呼んでも係数だけ覚え、次の発音（startAmbientNodes）へ反映される。
   * mute/unlock の管轄は変えない（master gain 経由のまま）。
   */
  setAmbientDuck(scale: number): void {
    const next = clamp01(scale);
    if (next === this.ambientDuck) return;
    this.ambientDuck = next;
    const nodes = this.ambientNodes;
    const ctx = this.ctx;
    if (!nodes || !ctx || !this.ambientMood) return;
    const drone = MOOD_AMBIENT[this.ambientMood];
    if (!drone) return;
    try {
      const now = ctx.currentTime;
      nodes.gain.gain.cancelScheduledValues(now);
      nodes.gain.gain.setValueAtTime(nodes.gain.gain.value, now);
      nodes.gain.gain.linearRampToValueAtTime(
        Math.max(drone.gain * next, 0.0001),
        now + AMBIENT_FADE_MS / 1000,
      );
    } catch {
      /* 減衰の失敗は無視（音量が変わらないだけ） */
    }
  }

  /** ドローンをフェードアウトして止める（内部用） */
  private stopAmbientNodes(): void {
    const nodes = this.ambientNodes;
    if (!nodes) return;
    this.ambientNodes = null;
    const ctx = this.ctx;
    if (!ctx) {
      // ctx が無ければ即時破棄
      for (const o of nodes.oscs) {
        try {
          o.stop();
          o.disconnect();
        } catch {
          /* 無視 */
        }
      }
      try {
        nodes.gain.disconnect();
      } catch {
        /* 無視 */
      }
      return;
    }
    try {
      const now = ctx.currentTime;
      const fade = AMBIENT_FADE_MS / 1000;
      nodes.gain.gain.cancelScheduledValues(now);
      nodes.gain.gain.setValueAtTime(nodes.gain.gain.value, now);
      nodes.gain.gain.linearRampToValueAtTime(0.0001, now + fade);
      for (const o of nodes.oscs) {
        o.stop(now + fade + 0.05);
        o.onended = () => {
          try {
            o.disconnect();
          } catch {
            /* 無視 */
          }
        };
      }
      // gain は最後のオシレータ終了後 GC される
    } catch {
      for (const o of nodes.oscs) {
        try {
          o.stop();
          o.disconnect();
        } catch {
          /* 無視 */
        }
      }
    }
  }

  // ========================================================================
  // BGM（HTMLAudio・master とは別系統だが mute/volume には連動）
  // ========================================================================

  /** BGM 要素を遅延生成（DOM 不在なら null） */
  private ensureBgmEl(): HTMLAudioElement | null {
    if (this.bgmEl) return this.bgmEl;
    if (typeof Audio === 'undefined') return null;
    try {
      const el = new Audio();
      el.preload = 'auto';
      this.bgmEl = el;
      return el;
    } catch {
      return null;
    }
  }

  /** master volume × BGM 個別音量を要素へ反映 */
  private applyBgmVolume(): void {
    if (!this.bgmEl) return;
    this.bgmEl.volume = clamp01(this.volume * this.bgmBaseVolume);
  }

  /** mute 状態に応じて BGM を pause / play する */
  private applyBgmMuteState(): void {
    const el = this.bgmEl;
    if (!el) return;
    if (this.muted) {
      el.pause();
    } else if (el.src) {
      // 解除時は再開（autoplay 制限下では失敗しうるが無視）
      void el.play().catch(() => {
        /* 無視 */
      });
    }
  }

  /**
   * BGM をアセット URL から再生する。url 空 / 失敗は無視（throw しない）。
   * master mute 中は src だけセットして pause のまま保持（解除で再生）。
   */
  playBgm(url: string, opts?: PlayBgmOptions): void {
    this.ensureStateLoaded();
    const trimmed = (url ?? '').trim();
    if (!trimmed) return;
    const el = this.ensureBgmEl();
    if (!el) return;

    this.bgmBaseVolume = clamp01(opts?.volume ?? 1);
    el.loop = opts?.loop ?? true;

    const fadeMs = opts?.fadeMs ?? DEFAULT_BGM_FADE_MS;

    // 同一 URL の再指定は src を張り替えない（再生中なら継続）。
    // el.src は常に絶対 URL で返るため、相対 URL（/api/v1/media/…）との比較は
    // 絶対化してから行う（P8-1: keep 通過後に同じ BGM を再宣言しても頭出ししない）。
    const sameSrc = !!el.src && el.src === toAbsoluteUrl(trimmed);
    if (!sameSrc) {
      el.src = trimmed;
    }

    if (this.muted) {
      // mute 中は鳴らさない。volume だけ整え、再生は解除時に行う
      this.applyBgmVolume();
      return;
    }

    if (sameSrc && !el.paused) {
      // 既に同じ曲が鳴っている: フェードをやり直さず音量/ループだけ整えて継続する
      // （scene A → spot_gate(keep) → scene A のような再宣言で音が凹まないように）。
      this.cancelBgmFade();
      this.applyBgmVolume();
      return;
    }

    // フェードイン
    this.cancelBgmFade();
    const target = clamp01(this.volume * this.bgmBaseVolume);
    el.volume = fadeMs > 0 ? 0 : target;
    this.bgmRetryPending = false;
    void el.play().catch(() => {
      // autoplay 制限（unlock 前）・URL 不正。unlock() 時に再試行できるよう覚えておく
      // （従来はここで握りつぶして終わり＝unlock 後も鳴らない死路だった）。
      this.bgmRetryPending = true;
    });
    if (fadeMs > 0) {
      this.fadeBgmTo(target, fadeMs);
    }
  }

  /** P8-1: unlock 時に、reject されたままの BGM 再生を再試行する（内部用・冪等）。 */
  private retryPendingBgm(): void {
    if (!this.bgmRetryPending) return;
    const el = this.bgmEl;
    if (!el || !el.src) {
      this.bgmRetryPending = false;
      return;
    }
    // mute 中は解除時（applyBgmMuteState）が再生を担うので、ここでは何もしない。
    if (this.muted) return;
    this.bgmRetryPending = false;
    // フェードは既に完了している可能性が高いので、目標音量を直接整えてから再生する。
    this.applyBgmVolume();
    void el.play().catch(() => {
      // まだ制限下なら次の unlock（ジェスチャ）でもう一度試す。
      this.bgmRetryPending = true;
    });
  }

  /** BGM を停止する（フェードアウト後 pause + src 解放）。 */
  stopBgm(opts?: StopBgmOptions): void {
    this.bgmRetryPending = false;
    const el = this.bgmEl;
    if (!el) return;
    const fadeMs = opts?.fadeMs ?? DEFAULT_BGM_FADE_MS;
    this.cancelBgmFade();
    if (fadeMs <= 0) {
      el.pause();
      el.removeAttribute('src');
      try {
        el.load();
      } catch {
        /* 無視 */
      }
      return;
    }
    this.fadeBgmTo(0, fadeMs, () => {
      el.pause();
      el.removeAttribute('src');
      try {
        el.load();
      } catch {
        /* 無視 */
      }
    });
  }

  /** requestAnimationFrame で BGM volume を線形フェード（WebAudio に依存しない） */
  private fadeBgmTo(target: number, durationMs: number, onDone?: () => void): void {
    const el = this.bgmEl;
    if (!el) return;
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      el.volume = clamp01(target);
      onDone?.();
      return;
    }
    const start = el.volume;
    const delta = target - start;
    const startTs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const tick = (): void => {
      const nowTs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const t = Math.min(1, (nowTs - startTs) / durationMs);
      el.volume = clamp01(start + delta * t);
      if (t < 1) {
        this.bgmFadeRaf = window.requestAnimationFrame(tick);
      } else {
        this.bgmFadeRaf = null;
        onDone?.();
      }
    };
    this.bgmFadeRaf = window.requestAnimationFrame(tick);
  }

  /** 進行中の BGM フェードを中断 */
  private cancelBgmFade(): void {
    if (this.bgmFadeRaf != null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.bgmFadeRaf);
    }
    this.bgmFadeRaf = null;
  }
}

/**
 * heartbeat の内部実装。setInterval 1 本で AudioEngine.beatPulse を叩く。
 * mood により基準周波数を決める（dread はより低く重い鼓動）。
 */
class InternalHeartbeat implements HeartbeatHandle {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private bpm: number;
  private readonly baseFreq: number;
  private stopped = false;

  constructor(
    private readonly engine: AudioEngine,
    bpm: number,
    mood?: Mood,
  ) {
    this.bpm = this.normalizeBpm(bpm);
    // mood ごとの基準周波数（Template15 既定は 60Hz）。dread/melancholy は低く重く。
    this.baseFreq = this.freqForMood(mood);
  }

  private normalizeBpm(bpm: number): number {
    if (!Number.isFinite(bpm) || bpm <= 0) return DEFAULT_HEARTBEAT_BPM;
    // 30..220 にクランプ（現実的な鼓動レンジ）
    return Math.max(30, Math.min(220, bpm));
  }

  private freqForMood(mood?: Mood): number {
    switch (mood) {
      case 'dread':
        return 52;
      case 'melancholy':
        return 56;
      case 'tension':
        return 64;
      case 'mystic':
        return 68;
      default:
        return 60;
    }
  }

  /** BPM から interval [ms] を算出（60000 / bpm） */
  private periodMs(): number {
    return Math.round(60000 / this.bpm);
  }

  start(): void {
    if (this.stopped) return;
    this.arm();
  }

  private arm(): void {
    if (typeof window === 'undefined') return; // タイマー不在環境では何もしない
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
    }
    // 最初の 1 拍は即時（待たせない）
    this.engine.beatPulse(this.baseFreq);
    this.intervalId = setInterval(() => {
      this.engine.beatPulse(this.baseFreq);
    }, this.periodMs());
  }

  setBpm(bpm: number): void {
    if (this.stopped) return;
    const next = this.normalizeBpm(bpm);
    if (next === this.bpm) return;
    this.bpm = next;
    // interval を新しい周期で張り直す
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
      this.intervalId = setInterval(() => {
        this.engine.beatPulse(this.baseFreq);
      }, this.periodMs());
    }
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    if (this.intervalId != null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.engine.forgetHeartbeat(this);
  }
}

/**
 * グローバル・シングルトン。アプリ全体でこの 1 インスタンスを共有する。
 * import しただけでは AudioContext は生成されない（unlock() まで遅延）。
 */
export const audioEngine = new AudioEngine();
